import { useEffect, useState, useRef } from "react";

const getBase = () =>
  process.env.REACT_APP_TEST_MODE === "true"
    ? ""
    : process.env.REACT_APP_S3_BASE_URL || "";

const useManifest = (section = "home") => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const intervalRef = useRef(null);
  const knownSlugsRef = useRef(null); // null = initial load not yet done
  const pollInterval = parseInt(process.env.REACT_APP_POLL_INTERVAL_MS || "60000", 10);

  useEffect(() => {
    knownSlugsRef.current = null; // reset on section change
    setArticles([]);

    let cancelled = false;

    const fetchArticles = async () => {
      const isFirstFetch = knownSlugsRef.current === null;

      try {
        const base = getBase();
        if (process.env.NODE_ENV === "development" && base) {
          console.log("syncing with S3..");
        }
        const res = await fetch(`${base}/search-index.json`);
        if (cancelled) return;
        if (!res.ok) throw new Error("Failed to load article index");

        const allEntries = await res.json();
        if (process.env.NODE_ENV === "development" && base) {
          console.log(`${allEntries.length} articles fetched from S3`);
        }
        if (cancelled) return;

        // Filter by section (or return all for "home")
        const entries = section === "home"
          ? allEntries
          : allEntries.filter((e) => e.section === section);

        if (entries.length === 0) {
          if (isFirstFetch) {
            setError("");
            setLoading(false);
          }
          return;
        }

        const currentSlugs = entries.map((e) => e.slug);

        // Diff: find entries we haven't seen yet
        const newEntries = isFirstFetch
          ? entries
          : entries.filter((e) => !knownSlugsRef.current.includes(e.slug));

        // Nothing new — skip all state updates silently
        if (!isFirstFetch && newEntries.length === 0) return;

        if (isFirstFetch) setLoading(true);

        const newArticles = newEntries.map((e) => ({
          title: e.title || "",
          abstract: e.abstract || "",
          date: e.date || "",
          section: e.section || section,
          author: e.author || "",
          slug: e.slug,
          url: `/content/articles/${e.slug}`,
          isNew: !isFirstFetch,
        }));

        if (cancelled) return;

        knownSlugsRef.current = currentSlugs;

        const sortByNewest = (arr) => [...arr].sort((a, b) => new Date(b.date) - new Date(a.date));
        setArticles((prev) => sortByNewest(isFirstFetch ? newArticles : [...newArticles, ...prev]));
        setError("");
      } catch (err) {
        if (!cancelled && isFirstFetch) {
          setError(err.message || "Something went wrong");
        }
      } finally {
        if (!cancelled && isFirstFetch) setLoading(false);
      }
    };

    fetchArticles();
    intervalRef.current = setInterval(fetchArticles, pollInterval);
    return () => {
      cancelled = true;
      clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  return { articles, loading, error };
};

export default useManifest;
